import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Flame,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trophy,
  RotateCcw,
  Target,
  User,
  Play,
  ArrowRight,
  Zap,
  ShieldCheck,
  Flag,
  SkipForward,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import DevMathTestPanel from "@/components/DevMathTestPanel";
import {
  inferQuestionType,
  MultiSelectQuestion,
  FillBlankQuestion,
  MatchingQuestion,
  gradeFillBlankAnswer
} from "@/components/tei";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Math subjects that use symbolic evaluation
const MATH_SUBJECTS = ['mathematics', 'math', 'algebra', 'calculus', 'geometry', 'trigonometry'];

function isMathSubject(subject) {
  if (!subject) return false;
  return MATH_SUBJECTS.includes(subject.toLowerCase().trim());
}

// ==================== ATTEMPT TRACKING HELPERS ====================

async function startAttemptSession(challengeId) {
  try {
    const response = await axios.post(`${API}/attempts/start`, {
      challenge_id: challengeId
    });
    return response.data.session_id;
  } catch (error) {
    console.error("Failed to start attempt session:", error);
    return null;
  }
}

async function logAttemptEvent(sessionId, questionIndex, answerCorrect, streakCanChange, timeSpentSeconds) {
  if (!sessionId) return;
  try {
    await axios.post(`${API}/attempts/event`, {
      session_id: sessionId,
      question_index: questionIndex,
      answer_correct: answerCorrect,
      streak_can_change: streakCanChange,
      time_spent_seconds: timeSpentSeconds
    });
  } catch (error) {
    console.error("Failed to log attempt event:", error);
  }
}

async function finishAttemptSession(sessionId, reachedGoal, longestStreak, longestStreakOverall, timeToGoalSeconds, totalTimeSeconds) {
  if (!sessionId) return;
  try {
    await axios.post(`${API}/attempts/finish`, {
      session_id: sessionId,
      reached_goal: reachedGoal,
      longest_streak: longestStreak,
      longest_streak_overall: longestStreakOverall, // Includes bonus streak
      time_to_goal_seconds: timeToGoalSeconds,
      total_time_seconds: totalTimeSeconds
    });
  } catch (error) {
    console.error("Failed to finish attempt session:", error);
  }
}

// Real-time progress update for dashboard persistence (uses session_id only)
async function updateAttemptProgress(sessionId, progressData) {
  if (!sessionId) return;
  try {
    await axios.post(`${API}/attempts/progress`, {
      session_id: sessionId,
      ...progressData
    });
  } catch (error) {
    console.error("Failed to update attempt progress:", error);
  }
}

// SendBeacon version for unload events (cannot use async/await)
function sendProgressBeacon(sessionId, progressData) {
  if (!sessionId) return;
  const payload = JSON.stringify({
    session_id: sessionId,
    ...progressData
  });
  // Use Blob with correct content-type for sendBeacon
  const blob = new Blob([payload], { type: 'application/json' });
  navigator.sendBeacon(`${API}/attempts/progress`, blob);
}

export default function ChallengePage() {
  const { challengeId } = useParams();
  const navigate = useNavigate();

  // DEV-ONLY: Version info state
  const [versionInfo, setVersionInfo] = useState(null);
  const isDev = process.env.NODE_ENV === 'development';

  // Challenge data
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Student state
  const [nickname, setNickname] = useState("");
  const [gameState, setGameState] = useState("intro"); // intro, playing, success, keepgoing

  // Quiz state
  const [questionPool, setQuestionPool] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [totalAnswered, setTotalAnswered] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  
  // TEI (Technology Enhanced Items) state
  const [teiMultiSelectAnswers, setTeiMultiSelectAnswers] = useState([]);  // For multi_select
  const [teiFillBlankAnswer, setTeiFillBlankAnswer] = useState('');        // For fill_blank
  const [teiMatchingAnswers, setTeiMatchingAnswers] = useState({});        // For matching

  // CRITICAL FRONTEND AUTHORITY FIX:
  // Backend is the SINGLE SOURCE OF TRUTH for correctness.
  // These states track backend response, NOT client-side inference.
  const [backendCorrectOption, setBackendCorrectOption] = useState(null); // From backend, not inference
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(null); // null = not answered, true/false from comparison
  const [streakProtected, setStreakProtected] = useState(false);
  const [streakSafeMessage, setStreakSafeMessage] = useState(null);
  const [needsReview, setNeedsReview] = useState(false);
  const [isNonGradable, setIsNonGradable] = useState(false);
  const [frontendInvariantViolation, setFrontendInvariantViolation] = useState(false);

  // Keep Going mode state
  const [bonusStreak, setBonusStreak] = useState(0);
  const [showBonusEndMessage, setShowBonusEndMessage] = useState(false);
  const [bonusEndedAt, setBonusEndedAt] = useState(0);

  // Stats - longestStreakOverall includes bonus streak beyond goal
  const [stats, setStats] = useState({
    totalQuestions: 0,
    correctAnswers: 0,
    longestStreak: 0,
    longestStreakOverall: 0, // Includes bonus streak
  });

  // Attempt tracking state
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [goalReachedTime, setGoalReachedTime] = useState(null);
  const [timeToGoalSeconds, setTimeToGoalSeconds] = useState(null); // Stored when goal reached, used when session ends
  
  // Progress throttling - prevent spam
  const lastProgressUpdateRef = useRef(0);
  const questionsAnsweredSinceProgressRef = useRef(0);
  const PROGRESS_THROTTLE_MS = 1500; // Min time between progress calls
  const PROGRESS_THROTTLE_QUESTIONS = 2; // Or after N questions
  
  // Guard to ensure finishAttemptSession is only called ONCE per session
  const hasFinishedRef = useRef(false);
  
  // Memoized finish function that respects the guard
  const finishSessionOnce = useCallback((reachedGoal, longestStreak, longestStreakOverall, timeToGoal, totalTime) => {
    if (hasFinishedRef.current || !sessionId) return;
    hasFinishedRef.current = true;
    finishAttemptSession(sessionId, reachedGoal, longestStreak, longestStreakOverall, timeToGoal, totalTime);
  }, [sessionId]);
  
  // Send progress update (throttled) - uses session_id only
  const sendProgressUpdate = useCallback((progressData, force = false) => {
    if (!sessionId) return;
    
    const now = Date.now();
    const timeSinceLastUpdate = now - lastProgressUpdateRef.current;
    const questionsSinceUpdate = questionsAnsweredSinceProgressRef.current;
    
    // Throttle: only send if forced, or time passed, or enough questions answered
    if (force || timeSinceLastUpdate >= PROGRESS_THROTTLE_MS || questionsSinceUpdate >= PROGRESS_THROTTLE_QUESTIONS) {
      lastProgressUpdateRef.current = now;
      questionsAnsweredSinceProgressRef.current = 0;
      updateAttemptProgress(sessionId, progressData);
    }
  }, [sessionId]);

  // Teacher pilot mode (check URL param)
  const [isPilotTeacher, setIsPilotTeacher] = useState(false);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setIsPilotTeacher(params.get("teacher") === "1");
  }, []);

  // DEV-ONLY: Fetch version info from /api/health
  useEffect(() => {
    if (isDev) {
      const fetchVersionInfo = async () => {
        try {
          const response = await axios.get(`${API}/health`);
          setVersionInfo(response.data);
        } catch (err) {
          console.error("Failed to fetch version info:", err);
        }
      };
      fetchVersionInfo();
    }
  }, [isDev]);

  // Load challenge on mount
  useEffect(() => {
    const fetchChallenge = async () => {
      try {
        const response = await axios.get(`${API}/challenges/${challengeId}`);
        if (response.data.success) {
          setChallenge(response.data.challenge);
          setQuestionPool([...response.data.challenge.question_pool]);
        } else {
          setError("Challenge not found");
        }
      } catch (err) {
        console.error("Error fetching challenge:", err);
        if (err.response?.status === 404) {
          setError("Challenge not found. It may have expired or the link is invalid.");
        } else {
          setError("Failed to load challenge. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchChallenge();
  }, [challengeId]);

  const handleStartChallenge = async () => {
    // Reset the finish guard for new session
    hasFinishedRef.current = false;
    
    // Use questions in the exact stored order - no shuffling
    // This ensures all students see the same questions in the same order
    setCurrentQuestionIndex(0);
    setCurrentStreak(0);
    setTotalAnswered(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setStats({ totalQuestions: 0, correctAnswers: 0, longestStreak: 0, longestStreakOverall: 0 });
    
    // Start attempt tracking session
    const newSessionId = await startAttemptSession(challengeId);
    setSessionId(newSessionId);
    const now = Date.now();
    setSessionStartTime(now);
    setQuestionStartTime(now);
    setGoalReachedTime(null);
    setTimeToGoalSeconds(null);
    
    // Reset TEI state
    setTeiMultiSelectAnswers([]);
    setTeiFillBlankAnswer('');
    setTeiMatchingAnswers({});
    
    setGameState("playing");
  };

  const currentQuestion = questionPool[currentQuestionIndex];
  
  // SINGLE SOURCE OF TRUTH: Use inferQuestionType for TEI rendering
  const currentQuestionType = useMemo(() => {
    if (!currentQuestion) return 'multiple_choice';
    return inferQuestionType(currentQuestion);
  }, [currentQuestion]);

  const handleSelectAnswer = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  // Teacher flag handler
  const handleTeacherFlag = async () => {
    try {
      await axios.post(`${API}/math/teacher-flag`, {
        challenge_id: challengeId,
        question_index: currentQuestionIndex,
        student_answer: currentQuestion.options[selectedAnswer],
        reason: "Teacher verified correct"
      });
      
      // Don't modify streak in keepgoing mode
      if (gameState === "keepgoing") {
        const newBonusStreak = bonusStreak + 1;
        setBonusStreak(newBonusStreak);
        setStreakProtected(false);
        setStreakSafeMessage(null);
        toast.success("Answer verified! Bonus streak restored.", { duration: 2000 });
        return;
      }
      
      // Restore streak in playing mode
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setStreakProtected(false);
      setStreakSafeMessage(null);
      toast.success("Answer verified! Streak restored.", { duration: 2000 });
      
      // Check for win
      if (newStreak >= challenge.streak_target) {
        setTimeout(() => {
          setStats((prev) => ({ ...prev, longestStreak: newStreak, longestStreakOverall: Math.max(prev.longestStreakOverall, newStreak) }));
          setGameState("success");
        }, 1500);
      }
    } catch (error) {
      console.error("Error flagging answer:", error);
      toast.error("Failed to verify answer");
    }
  };

  const handleSubmitAnswer = async () => {
    // ==================== TEI-AWARE ANSWER VALIDATION ====================
    // Check if we have an answer based on question type
    const hasAnswer = (() => {
      switch (currentQuestionType) {
        case 'multi_select':
          return teiMultiSelectAnswers.length > 0;
        case 'fill_blank':
          return teiFillBlankAnswer.trim() !== '';
        case 'matching':
          const pairCount = currentQuestion?.pairs?.length || 
                           currentQuestion?.matching_pairs?.length || 
                           currentQuestion?.left_column?.length || 0;
          return Object.keys(teiMatchingAnswers).length >= pairCount;
        default:
          return selectedAnswer !== null;
      }
    })();
    
    if (!hasAnswer) return;

    // ==================== CRITICAL: STATE RESET ====================
    setStreakProtected(false);
    setStreakSafeMessage(null);
    setNeedsReview(false);
    setIsNonGradable(false);
    setFrontendInvariantViolation(false);
    setIsCorrectAnswer(null);
    
    // ==================== TEI GRADING LOGIC ====================
    // For TEI question types, use simplified local grading
    if (currentQuestionType !== 'multiple_choice') {
      let answerCorrect = false;
      
      switch (currentQuestionType) {
        case 'multi_select': {
          const correctOptions = currentQuestion.correct_options || [currentQuestion.correct_option];
          // Check if arrays have same elements
          const sortedUser = [...teiMultiSelectAnswers].sort();
          const sortedCorrect = [...correctOptions].sort();
          answerCorrect = sortedUser.length === sortedCorrect.length && 
                         sortedUser.every((v, i) => v === sortedCorrect[i]);
          // TEI grading - multi_select
          if (isDev) console.log(`[TEI_RENDERER] multi_select grading: user=${JSON.stringify(sortedUser)}, correct=${JSON.stringify(sortedCorrect)}, result=${answerCorrect}`);
          break;
        }
        case 'fill_blank': {
          // Use fuzzy matching for forgiving grading (85% similarity threshold)
          // Supports: case-insensitive, trimmed whitespace, spelling tolerance, synonyms
          const correctAnswer = currentQuestion.correct_answer || 
                               currentQuestion.options?.[currentQuestion.correct_option] || '';
          const alternativeAnswers = currentQuestion.alternative_answers || [];
          
          const gradeResult = gradeFillBlankAnswer(teiFillBlankAnswer, correctAnswer, alternativeAnswers);
          answerCorrect = gradeResult.isCorrect;
          
          // Store similarity for potential UI feedback
          if (isDev) console.log(`[TEI_RENDERER] fill_blank grading result: ${gradeResult.isCorrect ? 'CORRECT' : 'INCORRECT'} (${(gradeResult.similarity * 100).toFixed(0)}% match)`);
          break;
        }
        case 'matching': {
          // All matches must be correct (leftIndex maps to same rightIndex)
          answerCorrect = Object.entries(teiMatchingAnswers).every(
            ([leftIdx, rightIdx]) => parseInt(leftIdx) === rightIdx
          );
          if (isDev) console.log(`[TEI_RENDERER] matching grading: matches=${JSON.stringify(teiMatchingAnswers)}, result=${answerCorrect}`);
          break;
        }
      }
      
      setIsCorrectAnswer(answerCorrect);
      setShowResult(true);
      setTotalAnswered((prev) => prev + 1);
      
      // ==================== CRITICAL: Handle TEI in Keep Going mode separately ====================
      if (gameState === "keepgoing") {
        // Bonus mode TEI handling - update bonus streak, NOT currentStreak
        if (answerCorrect) {
          const newBonusStreak = bonusStreak + 1;
          setBonusStreak(newBonusStreak);
          
          // Update longestStreakOverall to include bonus streak
          const totalStreak = challenge.streak_target + newBonusStreak;
          setStats(prev => ({
            ...prev,
            totalQuestions: prev.totalQuestions + 1,
            correctAnswers: prev.correctAnswers + 1,
            longestStreakOverall: Math.max(prev.longestStreakOverall, totalStreak)
          }));
          
          // Send progress update
          const totalTimeSeconds = sessionStartTime ? (Date.now() - sessionStartTime) / 1000 : null;
          sendProgressUpdate({
            current_streak: totalStreak,
            longest_streak: challenge.streak_target,
            longest_streak_overall: totalStreak,
            questions_answered: totalAnswered + 1,
            correct_answers: stats.correctAnswers + 1,
            reached_goal: true,
            time_to_goal_seconds: timeToGoalSeconds,
            total_time_seconds: totalTimeSeconds
          });
          
          toast.success(`Correct! Bonus streak: ${newBonusStreak}`, { duration: 1500 });
        } else {
          // Bonus mode ended on TEI question
          setBonusEndedAt(bonusStreak);
          setShowBonusEndMessage(true);
          setBonusStreak(0);
          
          setStats(prev => ({
            ...prev,
            totalQuestions: prev.totalQuestions + 1,
          }));
          
          // Finish the attempt session
          if (sessionId && sessionStartTime) {
            const totalTimeSeconds = (Date.now() - sessionStartTime) / 1000;
            finishSessionOnce(true, stats.longestStreak, stats.longestStreakOverall, timeToGoalSeconds, totalTimeSeconds);
          }
        }
        
        // Log attempt event for TEI in bonus mode
        const timeSpentSeconds = questionStartTime ? (Date.now() - questionStartTime) / 1000 : null;
        logAttemptEvent(sessionId, currentQuestionIndex, answerCorrect, true, timeSpentSeconds);
        
        return; // Exit early for TEI in keepgoing mode
      }
      
      // ==================== Normal playing mode TEI handling ====================
      // Update stats and streak (simplified TEI flow)
      const newStreakValue = answerCorrect ? currentStreak + 1 : 0;
      
      setStats((prev) => ({
        totalQuestions: prev.totalQuestions + 1,
        correctAnswers: prev.correctAnswers + (answerCorrect ? 1 : 0),
        longestStreak: Math.max(prev.longestStreak, newStreakValue),
        longestStreakOverall: Math.max(prev.longestStreakOverall, newStreakValue),
      }));
      
      if (answerCorrect) {
        setCurrentStreak(newStreakValue);
        toast.success(`Correct! Streak: ${newStreakValue}/${challenge.streak_target}`, { duration: 1500 });
        
        if (newStreakValue >= challenge.streak_target) {
          // Track goal reached time
          const goalTime = Date.now();
          setGoalReachedTime(goalTime);
          const computedTimeToGoal = sessionStartTime ? (goalTime - sessionStartTime) / 1000 : null;
          
          setTimeout(() => {
            setStats((prev) => ({ ...prev, longestStreak: newStreakValue, longestStreakOverall: Math.max(prev.longestStreakOverall, newStreakValue) }));
            setTimeToGoalSeconds(computedTimeToGoal);
            setGameState("success");
          }, 1500);
        }
      } else {
        setCurrentStreak(0);
        toast.error("Let's try again — streak reset", { duration: 2000 });
      }
      
      // Log attempt event
      const timeSpentSeconds = questionStartTime ? (Date.now() - questionStartTime) / 1000 : null;
      logAttemptEvent(sessionId, currentQuestionIndex, answerCorrect, true, timeSpentSeconds);
      
      return; // Exit early for TEI questions
    }
    
    // ==================== STANDARD MULTIPLE CHOICE GRADING (existing code) ====================
    // Below is the original logic for multiple choice questions
    
    // ==================== BACKEND AUTHORITY RULE ====================
    // The backend's `correct_option` is the ONLY source of truth.
    const backendCorrectIdx = currentQuestion.correct_option;
    setBackendCorrectOption(backendCorrectIdx);
    
    // ==================== SINGLE AUTHORITY FLAG ====================
    // streak_can_change and answer_correct from backend are the ONLY flags
    // Frontend MUST NOT independently reason about correctness or streak
    let streakCanChange = true;  // Default, will be overridden by backend
    let answerCorrect = null;    // Will be set by backend response
    
    // For math subjects, call backend to get streak_can_change and answer_correct
    if (isMathSubject(challenge?.subject)) {
      try {
        const mathCheckResponse = await axios.post(`${API}/math/check-answer`, {
          question_text: currentQuestion.question_text,
          correct_answer: currentQuestion.options[backendCorrectIdx],
          student_answer: currentQuestion.options[selectedAnswer],
          explanation: currentQuestion.explanation,
          challenge_id: challengeId,
          subject: challenge.subject,
          options: currentQuestion.options,
          correct_option_index: backendCorrectIdx,
          needs_streak_safe: currentQuestion.needs_streak_safe || false,
          streak_reset_allowed: currentQuestion.streak_reset_allowed !== false,
          is_gradable: currentQuestion.is_gradable !== false,
          advanced_math_beta: challenge.advanced_math_beta || false
        });

        const mathResult = mathCheckResponse.data;
        
        // ==================== ADVANCED MATH DETECTION ====================
        // The backend tells us if this question is detected as advanced math
        const advancedMathDetected = mathResult.advanced_math_detected === true;
        const creatorEnabledAdvanced = challenge?.advanced_math_beta === true;
        const advancedModeActive = creatorEnabledAdvanced || advancedMathDetected;
        
        // ==================== SINGLE AUTHORITY: streak_can_change ====================
        // This is the ONLY field frontend uses for streak decisions
        streakCanChange = mathResult.streak_can_change === true;
        
        // ==================== SINGLE AUTHORITY: answer_correct ====================
        // This is the ONLY field frontend uses for correctness UI
        answerCorrect = mathResult.answer_correct;
        
        // ==================== SKIP BANNER DISPLAY LOGIC ====================
        // Only show skip/protection UI if:
        // 1. streak cannot change, AND
        // 2. advanced_mode_active is true (creator toggle OR auto-detected)
        // Basic math with uncertain grading should NOT show skip banner
        if (!streakCanChange && advancedModeActive) {
          setIsNonGradable(true);
          setStreakProtected(true);
          // Use beta-specific message if creator enabled it, otherwise auto-detected message
          const betaMessage = "We're still improving advanced math grading — your streak is protected.";
          const autoDetectedMessage = "Complex math detected — your streak is protected while we verify.";
          const defaultMessage = mathResult.message || (creatorEnabledAdvanced ? betaMessage : autoDetectedMessage);
          setStreakSafeMessage(defaultMessage);
          setNeedsReview(mathResult.needs_review || false);
        } else if (!streakCanChange && !advancedModeActive) {
          // Basic math where grading is uncertain - just use the answer without skip banner
          // Log this case for monitoring
          if (isDev) console.log("[SKIP_GUARD] Basic math uncertain grading - NOT showing skip banner");
          // Still allow the backend's answer_correct to drive correctness
          setIsNonGradable(false);
          setStreakProtected(false);
          setStreakSafeMessage(null);
          // OVERRIDE: For basic math, allow streak to change based on local correctness
          // This ensures elementary math never shows skip behavior
          streakCanChange = true;
          answerCorrect = selectedAnswer === backendCorrectIdx;
        }
        
        // Log for debugging (explanation suppression)
        if (mathResult.explanation_suppressed) {
          console.warn("EXPLANATION_SUPPRESSED:", mathResult.suppression_reason);
        }
        
      } catch (error) {
        console.error("Math check error:", error);
        // On API error, check if this is advanced math
        const creatorEnabledAdvanced = challenge?.advanced_math_beta === true;
        // For API errors, we can't detect advanced math - use creator toggle as guide
        if (creatorEnabledAdvanced) {
          // Advanced mode - protect streak
          streakCanChange = false;
          setIsNonGradable(true);
          setStreakProtected(true);
          setStreakSafeMessage("We're still improving advanced math grading — your streak is protected.");
        } else {
          // Not advanced mode - grade locally without skip banner
          if (isDev) console.log("[SKIP_GUARD] API error on non-advanced math - grading locally");
          answerCorrect = selectedAnswer === backendCorrectIdx;
          streakCanChange = true;
          setIsNonGradable(false);
          setStreakProtected(false);
          setStreakSafeMessage(null);
        }
      }
    } else {
      // ==================== NON-MATH SUBJECTS: ALWAYS GRADE NORMALLY ====================
      // CRITICAL: Non-math subjects should NEVER trigger skip logic
      // This is a safety guard even if backend accidentally sets flags
      answerCorrect = selectedAnswer === backendCorrectIdx;
      
      // Non-math subjects: ALWAYS allow streak changes, ignore any flags
      // The backend should never set these for non-math, but we double-check here
      streakCanChange = true;
      
      // Log if we're overriding any flags (indicates backend bug)
      if (currentQuestion.needs_streak_safe || 
          currentQuestion.streak_reset_allowed === false ||
          currentQuestion.is_gradable === false ||
          currentQuestion.invariant_triggered) {
        console.warn(`[SKIP_GUARD] Non-math subject "${challenge?.subject}" had skip flags set - IGNORING them`);
        console.warn(`[SKIP_GUARD] Flags were: needs_streak_safe=${currentQuestion.needs_streak_safe}, streak_reset_allowed=${currentQuestion.streak_reset_allowed}, is_gradable=${currentQuestion.is_gradable}, invariant_triggered=${currentQuestion.invariant_triggered}`);
      }
      
      // NEVER set non-gradable or protected for non-math subjects
      setIsNonGradable(false);
      setStreakProtected(false);
      setStreakSafeMessage(null);
    }
    
    // ==================== FINAL CORRECTNESS DETERMINATION ====================
    // Use answerCorrect from API (for math) or local comparison (for non-math)
    // This is the ONLY source of truth for UI correctness display
    setIsCorrectAnswer(answerCorrect);
    
    // ==================== LOG ATTEMPT EVENT ====================
    // Calculate time spent on this question
    const timeSpentSeconds = questionStartTime ? (Date.now() - questionStartTime) / 1000 : null;
    logAttemptEvent(sessionId, currentQuestionIndex, answerCorrect, streakCanChange, timeSpentSeconds);
    
    // Increment questions answered counter for throttling
    questionsAnsweredSinceProgressRef.current += 1;
    
    setShowResult(true);
    setTotalAnswered((prev) => prev + 1);

    // ==================== STREAK UPDATE LOGIC ====================
    // SINGLE AUTHORITY: Use ONLY streakCanChange and answerCorrect for all decisions
    
    // Handle Keep Going mode (bonus mode after reaching goal)
    if (gameState === "keepgoing") {
      if (answerCorrect === true || !streakCanChange) {
        const newBonusStreak = bonusStreak + 1;
        setBonusStreak(newBonusStreak);
        
        // Update longestStreakOverall to include bonus streak
        // Total streak = original goal + bonus streak
        const totalStreak = challenge.streak_target + newBonusStreak;
        setStats(prev => ({
          ...prev,
          longestStreakOverall: Math.max(prev.longestStreakOverall, totalStreak)
        }));
        
        // Send throttled progress update during bonus mode
        const totalTimeSeconds = sessionStartTime ? (Date.now() - sessionStartTime) / 1000 : null;
        sendProgressUpdate({
          current_streak: totalStreak,
          longest_streak: challenge.streak_target,
          longest_streak_overall: totalStreak,
          questions_answered: totalAnswered + 1,
          correct_answers: stats.correctAnswers + (answerCorrect === true ? 1 : 0),
          reached_goal: true,
          time_to_goal_seconds: timeToGoalSeconds,
          total_time_seconds: totalTimeSeconds
        });
        
        if (!streakCanChange) {
          toast.success(streakSafeMessage || "This question is under review — it won't affect your streak.", { duration: 2000 });
        } else {
          toast.success(`Correct! Bonus streak: ${newBonusStreak}`, { duration: 1500 });
        }
      } else {
        // streakCanChange === true AND answerCorrect === false
        // Bonus mode ended - finish the attempt with bonus-inclusive streak
        setBonusEndedAt(bonusStreak);
        setShowBonusEndMessage(true);
        setBonusStreak(0);
        
        // NOW finish the attempt session with the correct bonus-inclusive stats
        if (sessionId && sessionStartTime) {
          const totalTimeSeconds = (Date.now() - sessionStartTime) / 1000;
          finishSessionOnce(true, stats.longestStreak, stats.longestStreakOverall, timeToGoalSeconds, totalTimeSeconds);
        }
      }
      return;
    }

    // Normal challenge mode
    // effectiveCorrect: answer is treated as correct if actually correct OR streak is protected
    const effectiveCorrect = answerCorrect === true || !streakCanChange;
    
    // Calculate what the new streak would be
    const newStreakValue = effectiveCorrect ? currentStreak + 1 : currentStreak;
    
    setStats((prev) => ({
      totalQuestions: prev.totalQuestions + 1,
      correctAnswers: prev.correctAnswers + (answerCorrect === true ? 1 : 0),
      longestStreak: Math.max(prev.longestStreak, newStreakValue),
      // longestStreakOverall tracks the highest streak including bonus mode
      longestStreakOverall: Math.max(prev.longestStreakOverall, newStreakValue),
    }));

    // Send throttled progress update after each question (will be forced if throttle threshold reached)
    const totalTimeSeconds = sessionStartTime ? (Date.now() - sessionStartTime) / 1000 : null;
    sendProgressUpdate({
      current_streak: newStreakValue,
      longest_streak: Math.max(stats.longestStreak, newStreakValue),
      longest_streak_overall: Math.max(stats.longestStreakOverall, newStreakValue),
      questions_answered: totalAnswered + 1,
      correct_answers: stats.correctAnswers + (answerCorrect === true ? 1 : 0),
      reached_goal: goalReachedTime !== null,
      time_to_goal_seconds: timeToGoalSeconds,
      total_time_seconds: totalTimeSeconds
    });

    // ==================== STREAK UPDATE WITH SINGLE AUTHORITY ====================
    // Use ONLY answerCorrect (from API) and streakCanChange (from API)
    if (answerCorrect === true) {
      // Correct answer: ALWAYS increment streak
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      
      if (!streakCanChange) {
        toast.success(streakSafeMessage || "This question is under review — it won't affect your streak.", { duration: 2000 });
      } else {
        toast.success(`Correct! Streak: ${newStreak}/${challenge.streak_target}`, { duration: 1500 });
      }

      if (newStreak >= challenge.streak_target) {
        // Track goal reached time
        const goalTime = Date.now();
        setGoalReachedTime(goalTime);
        const computedTimeToGoal = sessionStartTime ? (goalTime - sessionStartTime) / 1000 : null;
        
        // CRITICAL: Persist progress IMMEDIATELY when goal is reached
        // Many students stop at this moment - this ensures dashboard gets the data
        sendProgressUpdate({
          current_streak: newStreak,
          longest_streak: newStreak,
          longest_streak_overall: newStreak,
          questions_answered: totalAnswered + 1,
          correct_answers: stats.correctAnswers + 1,
          reached_goal: true,
          time_to_goal_seconds: computedTimeToGoal,
          total_time_seconds: computedTimeToGoal,
          last_event: "goal_reached"
        }, true); // force=true - don't throttle this critical update
        
        setTimeout(() => {
          setStats((prev) => ({ ...prev, longestStreak: newStreak, longestStreakOverall: Math.max(prev.longestStreakOverall, newStreak) }));
          // DO NOT finish attempt here - user may continue into bonus mode
          // Store timeToGoal for later use when session actually ends
          setTimeToGoalSeconds(computedTimeToGoal);
          setGameState("success");
        }, 1500);
      }
    } else {
      // Wrong answer (answerCorrect === false or null)
      // Reset streak ONLY if streakCanChange === true
      if (streakCanChange) {
        setCurrentStreak(0);
        toast.error("Let's try again — streak reset", { duration: 2000 });
      } else {
        // streakCanChange === false: NEVER reset streak, increment instead
        const newStreak = currentStreak + 1;
        setCurrentStreak(newStreak);
        toast.success(streakSafeMessage || "This question is under review — it won't affect your streak.", { duration: 2000 });
        
        if (newStreak >= challenge.streak_target) {
          // Track goal reached time
          const goalTime = Date.now();
          setGoalReachedTime(goalTime);
          const computedTimeToGoal = sessionStartTime ? (goalTime - sessionStartTime) / 1000 : null;
          
          // CRITICAL: Persist progress IMMEDIATELY when goal is reached
          sendProgressUpdate({
            current_streak: newStreak,
            longest_streak: newStreak,
            longest_streak_overall: newStreak,
            questions_answered: totalAnswered + 1,
            correct_answers: stats.correctAnswers,  // not +1 since this was protected, not actually correct
            reached_goal: true,
            time_to_goal_seconds: computedTimeToGoal,
            total_time_seconds: computedTimeToGoal,
            last_event: "goal_reached"
          }, true); // force=true
          
          setTimeout(() => {
            setStats((prev) => ({ ...prev, longestStreak: newStreak, longestStreakOverall: Math.max(prev.longestStreakOverall, newStreak) }));
            // DO NOT finish attempt here - user may continue into bonus mode
            // Store timeToGoal for later use when session actually ends
            setTimeToGoalSeconds(computedTimeToGoal);
            setGameState("success");
          }, 1500);
        }
      }
    }
  };

  const handleNextQuestion = () => {
    // ==================== CRITICAL: STATE RESET BETWEEN QUESTIONS ====================
    // All correctness state MUST be cleared to prevent persistence bugs
    setShowBonusEndMessage(false);
    setStreakProtected(false);
    setStreakSafeMessage(null);
    setNeedsReview(false);
    setIsNonGradable(false);
    setFrontendInvariantViolation(false);
    setBackendCorrectOption(null);
    setIsCorrectAnswer(null);
    setSelectedAnswer(null);
    setShowResult(false);
    
    // Reset TEI state for next question
    setTeiMultiSelectAnswers([]);
    setTeiFillBlankAnswer('');
    setTeiMatchingAnswers({});
    
    // Reset question start time for the next question
    setQuestionStartTime(Date.now());
    
    let nextIndex = currentQuestionIndex + 1;

    // If we've gone through all questions, loop back in the same order
    // No shuffling - maintains consistency across all students
    if (nextIndex >= questionPool.length) {
      nextIndex = 0;
    }

    setCurrentQuestionIndex(nextIndex);
  };

  const handleKeepGoing = () => {
    // ==================== STATE RESET FOR KEEP GOING MODE ====================
    setBonusStreak(0);
    setShowBonusEndMessage(false);
    setSelectedAnswer(null);  // CRITICAL: Reset selected answer
    setShowResult(false);     // CRITICAL: Reset show result
    setStreakProtected(false);
    setStreakSafeMessage(null);
    setNeedsReview(false);
    setIsNonGradable(false);
    setFrontendInvariantViolation(false);
    // Reset TEI state
    setTeiMultiSelectAnswers([]);
    setTeiFillBlankAnswer('');
    setTeiMatchingAnswers({});
    setBackendCorrectOption(null);
    setIsCorrectAnswer(null);
    // Continue from where we left off (next question after completion)
    const nextIndex = (currentQuestionIndex + 1) % questionPool.length;
    setCurrentQuestionIndex(nextIndex);
    // Reset question start time for the new question
    setQuestionStartTime(Date.now());
    setGameState("keepgoing");
  };

  const handlePlayAgain = () => {
    // ==================== FULL STATE RESET ====================
    setCurrentQuestionIndex(0);
    setCurrentStreak(0);
    setTotalAnswered(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setBonusStreak(0);
    setShowBonusEndMessage(false);
    setStats({ totalQuestions: 0, correctAnswers: 0, longestStreak: 0, longestStreakOverall: 0 });
    // Reset goal tracking
    setGoalReachedTime(null);
    setTimeToGoalSeconds(null);
    // Reset the finish guard for new session
    hasFinishedRef.current = false;
    // Reset all correctness state
    setStreakProtected(false);
    setStreakSafeMessage(null);
    setNeedsReview(false);
    setIsNonGradable(false);
    setFrontendInvariantViolation(false);
    setBackendCorrectOption(null);
    setIsCorrectAnswer(null);
    // Reset TEI state
    setTeiMultiSelectAnswers([]);
    setTeiFillBlankAnswer('');
    setTeiMatchingAnswers({});
    setGameState("playing");
  };

  const handleBackToIntro = () => {
    // ==================== FULL STATE RESET ====================
    // Persist progress and finish attempt session on quit (if session exists)
    if (sessionId && sessionStartTime) {
      const totalTimeSeconds = (Date.now() - sessionStartTime) / 1000;
      // Use reached_goal=true if they reached goal, with bonus-inclusive longestStreakOverall
      const reachedGoal = goalReachedTime !== null;
      
      // Send final progress update before finishing (forced)
      sendProgressUpdate({
        current_streak: currentStreak,
        longest_streak: stats.longestStreak,
        longest_streak_overall: stats.longestStreakOverall,
        questions_answered: totalAnswered,
        correct_answers: stats.correctAnswers,
        reached_goal: reachedGoal,
        time_to_goal_seconds: timeToGoalSeconds,
        total_time_seconds: totalTimeSeconds,
        last_event: "quit"
      }, true); // force=true
      
      finishSessionOnce(reachedGoal, stats.longestStreak, stats.longestStreakOverall, timeToGoalSeconds, totalTimeSeconds);
    }
    
    // Reset the finish guard for next session
    hasFinishedRef.current = false;
    
    setGameState("intro");
    setCurrentQuestionIndex(0);
    setCurrentStreak(0);
    setTotalAnswered(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setBonusStreak(0);
    setShowBonusEndMessage(false);
    // Reset all correctness state
    setStreakProtected(false);
    setStreakSafeMessage(null);
    setNeedsReview(false);
    setIsNonGradable(false);
    setFrontendInvariantViolation(false);
    setBackendCorrectOption(null);
    setIsCorrectAnswer(null);
    // Reset TEI state
    setTeiMultiSelectAnswers([]);
    setTeiFillBlankAnswer('');
    setTeiMatchingAnswers({});
    // Reset session tracking
    setSessionId(null);
    setSessionStartTime(null);
    setQuestionStartTime(null);
    setGoalReachedTime(null);
  };

  // ==================== BEST-EFFORT SESSION FINISH ON PAGE UNLOAD ====================
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't send if already finished
      if (hasFinishedRef.current) return;
      if (sessionId && sessionStartTime) {
        hasFinishedRef.current = true;
        const totalTimeSeconds = (Date.now() - sessionStartTime) / 1000;
        const reachedGoal = goalReachedTime !== null;
        
        // Send progress update via beacon FIRST (more reliable for dashboard)
        sendProgressBeacon(sessionId, {
          current_streak: currentStreak,
          longest_streak: stats.longestStreak,
          longest_streak_overall: stats.longestStreakOverall,
          questions_answered: totalAnswered,
          correct_answers: stats.correctAnswers,
          reached_goal: reachedGoal,
          time_to_goal_seconds: timeToGoalSeconds,
          total_time_seconds: totalTimeSeconds,
          last_event: "unload"
        });
        
        // Also send finish beacon for backward compatibility
        const finishPayload = JSON.stringify({
          session_id: sessionId,
          reached_goal: reachedGoal,
          longest_streak: stats.longestStreak,
          longest_streak_overall: stats.longestStreakOverall,
          time_to_goal_seconds: timeToGoalSeconds,
          total_time_seconds: totalTimeSeconds
        });
        navigator.sendBeacon(`${API}/attempts/finish`, finishPayload);
      }
    };

    const handleVisibilityChange = () => {
      // Don't send if already finished
      if (hasFinishedRef.current) return;
      if (document.visibilityState === "hidden" && sessionId && sessionStartTime) {
        hasFinishedRef.current = true;
        const totalTimeSeconds = (Date.now() - sessionStartTime) / 1000;
        const reachedGoal = goalReachedTime !== null;
        
        // Send progress update via beacon FIRST (more reliable for dashboard)
        sendProgressBeacon(sessionId, {
          current_streak: currentStreak,
          longest_streak: stats.longestStreak,
          longest_streak_overall: stats.longestStreakOverall,
          questions_answered: totalAnswered,
          correct_answers: stats.correctAnswers,
          reached_goal: reachedGoal,
          time_to_goal_seconds: timeToGoalSeconds,
          total_time_seconds: totalTimeSeconds,
          last_event: "visibility_hidden"
        });
        
        // Also send finish beacon for backward compatibility
        const finishPayload = JSON.stringify({
          session_id: sessionId,
          reached_goal: reachedGoal,
          longest_streak: stats.longestStreak,
          longest_streak_overall: stats.longestStreakOverall,
          time_to_goal_seconds: timeToGoalSeconds,
          total_time_seconds: totalTimeSeconds
        });
        navigator.sendBeacon(`${API}/attempts/finish`, finishPayload);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [sessionId, sessionStartTime, currentStreak, stats.longestStreak, stats.longestStreakOverall, stats.correctAnswers, totalAnswered, gameState, goalReachedTime, timeToGoalSeconds]);

  // DEV-ONLY Version Banner Component
  const DevBanner = () => {
    if (!isDev || !versionInfo) return null;
    return (
      <div 
        className="fixed top-0 left-0 right-0 bg-yellow-400 text-black px-4 py-2 text-xs font-mono z-50 flex flex-wrap items-center justify-center gap-4"
        data-testid="dev-version-banner"
      >
        <span><strong>API:</strong> {API}</span>
        <span><strong>Commit:</strong> {versionInfo.git_commit}</span>
        <span><strong>Build:</strong> {versionInfo.build_time_utc}</span>
        <span><strong>Env:</strong> {versionInfo.env_name}</span>
      </div>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <DevBanner />
        <DevMathTestPanel />
        <Card className="max-w-md w-full mx-4 rounded-2xl">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 text-ember mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">Loading Challenge...</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-8 px-4">
        <DevBanner />
        <DevMathTestPanel />
        <Card className="max-w-md w-full rounded-2xl">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-sage-900 mb-2">Oops!</h2>
            <p className="font-body text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => window.history.back()} className="rounded-full">
              Go Back
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Intro Screen (Student enters nickname and starts)
  if (gameState === "intro" && challenge) {
    return (
      <div className="min-h-screen bg-paper py-8 px-4">
        <DevBanner />
        <DevMathTestPanel />
        <div className="max-w-md mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="p-3 bg-ember/10 rounded-2xl">
                <Flame className="w-10 h-10 text-ember" />
              </div>
              <h1 className="font-heading text-4xl font-light text-sage-900">
                StreakChallenge
              </h1>
            </div>
            <p className="font-body text-lg text-muted-foreground">
              You've been invited to a challenge!
            </p>
          </div>

          {/* Challenge Info Card */}
          <Card className="border-border bg-card rounded-2xl shadow-lg mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-xl text-sage-900 flex items-center gap-2">
                <Target className="w-5 h-5 text-sage-500" />
                Challenge Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-secondary rounded-xl text-center">
                  <p className="font-heading text-lg text-sage-900">{challenge.subject}</p>
                  <p className="font-body text-xs text-muted-foreground">Subject</p>
                </div>
                <div className="p-4 bg-secondary rounded-xl text-center">
                  <p className="font-heading text-lg text-sage-900">{challenge.grade_level}</p>
                  <p className="font-body text-xs text-muted-foreground">Grade Level</p>
                </div>
              </div>

              <div className="p-4 bg-ember/10 rounded-xl text-center">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Flame className="w-6 h-6 text-ember" />
                  <p className="font-heading text-2xl text-ember">{challenge.streak_target}</p>
                </div>
                <p className="font-body text-sm text-muted-foreground">
                  correct answers in a row to win
                </p>
              </div>

              <p className="font-body text-sm text-center text-muted-foreground">
                {challenge.question_count} questions prepared for you
              </p>
            </CardContent>
          </Card>

          {/* Nickname Input */}
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardContent className="p-6 space-y-4">
              <div>
                <Label className="font-body">
                  Your Nickname (Optional)
                </Label>
                <div className="flex items-center gap-2 mt-2">
                  <User className="w-5 h-5 text-muted-foreground" />
                  <Input
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="Enter your name..."
                    className="flex-grow"
                    data-testid="nickname-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleStartChallenge}
                className="w-full rounded-full py-6 text-lg font-medium btn-press bg-ember hover:bg-ember/90 text-white"
                data-testid="start-challenge-btn"
              >
                <Play className="w-5 h-5 mr-2" />
                Start Challenge
              </Button>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="mt-6 text-center">
            <p className="font-body text-sm text-muted-foreground">
              Wrong answers reset your streak to 0.
              <br />
              Keep going until you get {challenge.streak_target} in a row!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success Screen
  if (gameState === "success" && challenge) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center py-8 px-4">
        <DevBanner />
        <DevMathTestPanel />
        <Card className="max-w-md w-full rounded-2xl shadow-lg overflow-hidden">
          <CardContent className="p-8 text-center">
            <div className="w-24 h-24 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-float">
              <Trophy className="w-12 h-12 text-amber-500" />
            </div>

            <div className="bg-gradient-to-br from-ember to-amber-500 text-white rounded-2xl p-6 mb-6">
              <h2 className="font-heading text-3xl mb-2">
                {nickname ? `${nickname} is a` : ""} Streak Master!
              </h2>
              <p className="font-body text-xl opacity-90">
                {challenge.streak_target} in a row!
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-secondary rounded-xl">
                <p className="font-heading text-2xl text-sage-900">{stats.totalQuestions}</p>
                <p className="font-body text-sm text-muted-foreground">Questions</p>
              </div>
              <div className="p-4 bg-secondary rounded-xl">
                <p className="font-heading text-2xl text-sage-900">
                  {stats.totalQuestions > 0 ? Math.round((stats.correctAnswers / stats.totalQuestions) * 100) : 0}%
                </p>
                <p className="font-body text-sm text-muted-foreground">Accuracy</p>
              </div>
            </div>

            <p className="font-body text-muted-foreground mb-6">
              {challenge.subject} • {challenge.grade_level}
            </p>

            <div className="space-y-3">
              <div>
                <Button
                  onClick={handleKeepGoing}
                  className="w-full rounded-full py-6 font-medium bg-ember hover:bg-ember/90 text-white"
                  data-testid="keep-going-btn"
                >
                  <ArrowRight className="w-5 h-5 mr-2" />
                  Keep Going
                </Button>
                <p className="font-body text-xs text-muted-foreground text-center mt-2">
                  Practice a little more and see how long your bonus streak lasts.
                </p>
              </div>
              {/* "New Challenge" button removed for students - creation only happens on creator portal */}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Keep Going Screen (playing in bonus mode)
  if (gameState === "keepgoing" && currentQuestion && challenge) {
    return (
      <div className="min-h-screen bg-paper" data-testid="keepgoing-screen">
        <DevBanner />
        <DevMathTestPanel />
        <header className="border-b border-border bg-card">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-sm text-muted-foreground">
                {nickname && <span className="text-sage-900 mr-2">{nickname}</span>}
                {challenge.subject} • {challenge.grade_level}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToIntro}
                className="text-muted-foreground hover:text-sage-900"
              >
                Quit
              </Button>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${bonusStreak > 0 ? "bg-purple-100" : "bg-muted"}`}>
                  <Zap className={`w-6 h-6 ${bonusStreak > 0 ? "text-purple-500" : "text-muted-foreground"}`} />
                </div>
                <div>
                  <p className="font-heading text-3xl text-sage-900">
                    {bonusStreak}
                  </p>
                  <p className="font-body text-xs text-purple-600">Bonus Streak</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-body text-sm text-green-600 flex items-center justify-end gap-1">
                  <CheckCircle className="w-4 h-4" />
                  Challenge Complete!
                </p>
                <p className="font-body text-xs text-muted-foreground">
                  Keep going for bonus points
                </p>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardContent className="p-6 sm:p-8">
              {/* Only show question text for non-fill_blank (fill_blank has it inline) */}
              {currentQuestionType !== 'fill_blank' && (
                <h2 className="font-body text-lg sm:text-xl text-sage-900 mb-6 leading-relaxed">
                  {currentQuestion.question_text}
                </h2>
              )}

              {/* ==================== TEI RENDERER SWITCH (BONUS MODE) ====================
                  Uses same rendering logic as playing mode */}
              <div className="mb-6">
                {(() => {
                  const type = currentQuestionType;
                  if (isDev) console.log(`[TEI_RENDERER] Bonus mode rendering: ${type}`);
                  
                  switch (type) {
                    case 'multi_select':
                      return (
                        <MultiSelectQuestion
                          question={currentQuestion}
                          selectedAnswers={teiMultiSelectAnswers}
                          onSelectionChange={setTeiMultiSelectAnswers}
                          showResult={showResult}
                          correctOptions={currentQuestion.correct_options || [currentQuestion.correct_option]}
                          disabled={showResult}
                        />
                      );
                    
                    case 'fill_blank':
                      return (
                        <FillBlankQuestion
                          question={currentQuestion}
                          userAnswer={teiFillBlankAnswer}
                          onAnswerChange={setTeiFillBlankAnswer}
                          showResult={showResult}
                          correctAnswer={currentQuestion.correct_answer || currentQuestion.options?.[currentQuestion.correct_option] || ''}
                          disabled={showResult}
                        />
                      );
                    
                    case 'matching':
                      return (
                        <MatchingQuestion
                          question={currentQuestion}
                          userMatches={teiMatchingAnswers}
                          onMatchChange={setTeiMatchingAnswers}
                          showResult={showResult}
                          disabled={showResult}
                        />
                      );
                    
                    case 'multiple_choice':
                    default:
                      return (
                        <div className="space-y-3">
                          {currentQuestion.options.map((option, index) => {
                            let optionClass = "quiz-option";
                            let icon = null;

                            if (showResult) {
                              const correctIdx = backendCorrectOption ?? currentQuestion.correct_option;
                              
                              if (isNonGradable || frontendInvariantViolation) {
                                if (index === selectedAnswer) {
                                  optionClass += " selected";
                                  icon = <ShieldCheck className="w-5 h-5 text-blue-500 flex-shrink-0" />;
                                }
                              } else {
                                if (index === correctIdx) {
                                  optionClass += " correct";
                                  icon = <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
                                } else if (index === selectedAnswer && index !== correctIdx) {
                                  optionClass += " incorrect";
                                  icon = <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
                                }
                              }
                            } else if (selectedAnswer === index) {
                              optionClass += " selected";
                            }

                            return (
                              <button
                                key={index}
                                onClick={() => handleSelectAnswer(index)}
                                disabled={showResult}
                                className={`w-full p-4 rounded-xl border-2 border-border text-left font-body flex items-center gap-3 ${optionClass}`}
                                data-testid={`keepgoing-option-${index}`}
                              >
                                <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-secondary-foreground flex-shrink-0">
                                  {String.fromCharCode(65 + index)}
                                </span>
                                <span className="flex-grow">{option}</span>
                                {icon}
                              </button>
                            );
                          })}
                        </div>
                      );
                  }
                })()}
              </div>

              {/* ==================== EXPLANATION DISPLAY RULE ====================
                  Explanation is INFORMATIONAL ONLY - it does NOT drive correctness UI */}
              {showResult && !isNonGradable && !frontendInvariantViolation && 
               selectedAnswer !== (backendCorrectOption ?? currentQuestion.correct_option) && 
               currentQuestion.explanation && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body font-medium text-amber-800 mb-1">Explanation</p>
                      <p className="font-body text-amber-700">{currentQuestion.explanation}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* PRODUCTION HOTFIX: Show skipped message for non-gradable in Keep Going mode */}
              {showResult && isNonGradable && streakProtected && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="question-skipped-banner">
                  <div className="flex items-start gap-3">
                    <SkipForward className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-body font-medium text-blue-800 mb-1">This question was skipped</p>
                      <p className="font-body text-blue-700">
                        We're still improving advanced math grading. This question won't affect your streak.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {showBonusEndMessage && (
                <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-purple-500 flex-shrink-0" />
                    <p className="font-body text-purple-700">
                      Bonus streak ended at {bonusEndedAt}. Keep going!
                    </p>
                  </div>
                </div>
              )}

              {!showResult ? (
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={(() => {
                    // TEI-aware answer check for bonus mode
                    switch (currentQuestionType) {
                      case 'multi_select':
                        return teiMultiSelectAnswers.length === 0;
                      case 'fill_blank':
                        return teiFillBlankAnswer.trim() === '';
                      case 'matching':
                        const pairCount = currentQuestion?.pairs?.length || 
                                         currentQuestion?.matching_pairs?.length || 
                                         currentQuestion?.left_column?.length || 0;
                        return Object.keys(teiMatchingAnswers).length < pairCount;
                      default:
                        return selectedAnswer === null;
                    }
                  })()}
                  className="w-full rounded-full py-6 font-medium btn-press bg-sage-900 hover:bg-sage-900/90 text-white"
                >
                  Submit Answer
                </Button>
              ) : (
                <Button
                  onClick={handleNextQuestion}
                  className="w-full rounded-full py-6 font-medium btn-press bg-purple-500 hover:bg-purple-600 text-white"
                >
                  Continue
                </Button>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // Playing Screen
  if (gameState === "playing" && currentQuestion && challenge) {
    const progress = (currentStreak / challenge.streak_target) * 100;

    return (
      <div className="min-h-screen bg-paper" data-testid="quiz-screen">
        <DevBanner />
        <DevMathTestPanel />
        <header className="border-b border-border bg-card">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-body text-sm text-muted-foreground">
                {nickname && <span className="text-sage-900 mr-2">{nickname}</span>}
                {challenge.subject} • {challenge.grade_level}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToIntro}
                className="text-muted-foreground hover:text-sage-900"
              >
                Quit
              </Button>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-xl ${currentStreak > 0 ? "bg-ember/10" : "bg-muted"}`}>
                  <Flame className={`w-6 h-6 ${currentStreak > 0 ? "text-ember" : "text-muted-foreground"}`} />
                </div>
                <div>
                  <p className="font-heading text-3xl text-sage-900">
                    {currentStreak}
                    <span className="text-lg text-muted-foreground">/{challenge.streak_target}</span>
                  </p>
                  <p className="font-body text-xs text-muted-foreground">Current Streak</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-body text-sm text-muted-foreground">
                  Target: {challenge.streak_target} in a row
                </p>
                <p className="font-body text-xs text-muted-foreground">
                  Q{totalAnswered + 1}
                </p>
              </div>
            </div>

            <Progress value={progress} className="h-3" />
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Card className="border-border bg-card rounded-2xl shadow-lg">
            <CardContent className="p-6 sm:p-8">
              {/* Only show question text for non-fill_blank (fill_blank has it inline) */}
              {currentQuestionType !== 'fill_blank' && (
                <h2 className="font-body text-lg sm:text-xl text-sage-900 mb-6 leading-relaxed">
                  {currentQuestion.question_text}
                </h2>
              )}

              {/* ==================== TEI RENDERER SWITCH ====================
                  SINGLE SOURCE OF TRUTH: currentQuestionType from inferQuestionType()
                  NO fallbacks to question_type field - only the computed type is used */}
              <div className="mb-6">
                {(() => {
                  const type = currentQuestionType;
                  if (isDev) console.log(`[TEI_RENDERER] Rendering question type: ${type}`);
                  
                  switch (type) {
                    case 'multi_select':
                      return (
                        <MultiSelectQuestion
                          question={currentQuestion}
                          selectedAnswers={teiMultiSelectAnswers}
                          onSelectionChange={setTeiMultiSelectAnswers}
                          showResult={showResult}
                          correctOptions={currentQuestion.correct_options || [currentQuestion.correct_option]}
                          disabled={showResult}
                        />
                      );
                    
                    case 'fill_blank':
                      return (
                        <FillBlankQuestion
                          question={currentQuestion}
                          userAnswer={teiFillBlankAnswer}
                          onAnswerChange={setTeiFillBlankAnswer}
                          showResult={showResult}
                          correctAnswer={currentQuestion.correct_answer || currentQuestion.options?.[currentQuestion.correct_option] || ''}
                          disabled={showResult}
                        />
                      );
                    
                    case 'matching':
                      return (
                        <MatchingQuestion
                          question={currentQuestion}
                          userMatches={teiMatchingAnswers}
                          onMatchChange={setTeiMatchingAnswers}
                          showResult={showResult}
                          disabled={showResult}
                        />
                      );
                    
                    case 'multiple_choice':
                    default:
                      // Standard multiple choice rendering
                      return (
                        <div className="space-y-3">
                          {currentQuestion.options.map((option, index) => {
                            let optionClass = "quiz-option";
                            let icon = null;

                            if (showResult) {
                              const correctIdx = backendCorrectOption ?? currentQuestion.correct_option;
                              
                              if (isNonGradable || frontendInvariantViolation) {
                                if (index === selectedAnswer) {
                                  optionClass += " selected";
                                  icon = <ShieldCheck className="w-5 h-5 text-blue-500 flex-shrink-0" />;
                                }
                              } else {
                                if (index === correctIdx) {
                                  optionClass += " correct";
                                  icon = <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />;
                                } else if (index === selectedAnswer && index !== correctIdx) {
                                  optionClass += " incorrect";
                                  icon = <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />;
                                }
                              }
                            } else if (selectedAnswer === index) {
                              optionClass += " selected";
                            }

                            return (
                              <button
                                key={index}
                                onClick={() => handleSelectAnswer(index)}
                                disabled={showResult}
                                className={`w-full p-4 rounded-xl border-2 border-border text-left font-body flex items-center gap-3 ${optionClass}`}
                                data-testid={`option-${index}`}
                              >
                                <span className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-medium text-secondary-foreground flex-shrink-0">
                                  {String.fromCharCode(65 + index)}
                                </span>
                                <span className="flex-grow">{option}</span>
                                {icon}
                              </button>
                            );
                          })}
                        </div>
                      );
                  }
                })()}
              </div>

              {/* ==================== EXPLANATION DISPLAY RULE ====================
                  Explanation is INFORMATIONAL ONLY - it does NOT drive correctness UI */}
              {showResult && !isNonGradable && !frontendInvariantViolation && 
               selectedAnswer !== (backendCorrectOption ?? currentQuestion.correct_option) && 
               currentQuestion.explanation && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-body font-medium text-amber-800 mb-1">Explanation</p>
                      <p className="font-body text-amber-700">{currentQuestion.explanation}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Streak-Safe Mode Indicator - Show when streak_can_change === false */}
              {showResult && (streakProtected || isNonGradable || frontendInvariantViolation) && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="question-skipped-banner">
                  <div className="flex items-start gap-3">
                    <SkipForward className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-body font-medium text-blue-800 mb-1" data-testid="skipped-title">This question was skipped</p>
                      <p className="font-body text-blue-700" data-testid="skipped-message">
                        We're still improving advanced math grading. This question won't affect your streak.
                      </p>
                      {isPilotTeacher && needsReview && (
                        <div className="mt-3">
                          <Button
                            onClick={handleTeacherFlag}
                            variant="outline"
                            size="sm"
                            className="rounded-full text-blue-700 border-blue-300 hover:bg-blue-100"
                            title="Flag this if the math is correct but was marked wrong. This helps improve accuracy for everyone."
                          >
                            <Flag className="w-4 h-4 mr-1" />
                            This answer is actually correct
                          </Button>
                          <p className="font-body text-xs text-blue-600 mt-1.5">
                            Flag this if the math is correct but was marked wrong. This helps improve accuracy for everyone.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ==================== STREAK RESET MESSAGE RULE ====================
                  HARD GUARD: Only show if ALL conditions met:
                  1. showResult is true (answer submitted)
                  2. isNonGradable is false (question is gradable)
                  3. frontendInvariantViolation is false
                  4. streakProtected is false (streak_can_change === true from API)
                  5. isCorrectAnswer is false (answer_correct === false from API)
                  
                  CRITICAL: If streak_can_change === false (streakProtected === true):
                  - NEVER show "Streak reset"
                  - NEVER show red banners
                  - Show neutral "under review" message instead
                  
                  We use isCorrectAnswer (from API's answer_correct) NOT selectedAnswer comparison */}
              {showResult && 
               !isNonGradable && 
               !frontendInvariantViolation && 
               !streakProtected &&
               isCorrectAnswer === false && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="streak-reset-banner">
                  <div className="flex items-start gap-3">
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-body text-red-700">Let's try again — streak reset. You've got this!</p>
                      {isPilotTeacher && (
                        <div className="mt-3">
                          <Button
                            onClick={handleTeacherFlag}
                            variant="outline"
                            size="sm"
                            className="rounded-full text-red-700 border-red-300 hover:bg-red-100"
                            title="Flag this if the math is correct but was marked wrong. This helps improve accuracy for everyone."
                          >
                            <Flag className="w-4 h-4 mr-1" />
                            This answer is actually correct
                          </Button>
                          <p className="font-body text-xs text-red-600 mt-1.5">
                            Flag this if the math is correct but was marked wrong. This helps improve accuracy for everyone.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ==================== CORRECT ANSWER MESSAGE RULE ====================
                  Only show if question is gradable AND isCorrectAnswer === true (from API) */}
              {showResult && !isNonGradable && !frontendInvariantViolation && !streakProtected &&
               isCorrectAnswer === true && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="correct-answer-banner">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p className="font-body text-green-700">
                      Correct! Streak: {currentStreak}/{challenge.streak_target}
                    </p>
                  </div>
                </div>
              )}

              {!showResult ? (
                <Button
                  onClick={handleSubmitAnswer}
                  disabled={(() => {
                    // TEI-aware answer check
                    switch (currentQuestionType) {
                      case 'multi_select':
                        return teiMultiSelectAnswers.length === 0;
                      case 'fill_blank':
                        return teiFillBlankAnswer.trim() === '';
                      case 'matching':
                        // Require all pairs to be matched
                        const pairCount = currentQuestion?.pairs?.length || 
                                         currentQuestion?.matching_pairs?.length || 
                                         currentQuestion?.left_column?.length || 0;
                        return Object.keys(teiMatchingAnswers).length < pairCount;
                      default:
                        return selectedAnswer === null;
                    }
                  })()}
                  className="w-full rounded-full py-6 font-medium btn-press bg-sage-900 hover:bg-sage-900/90 text-white"
                >
                  Submit Answer
                </Button>
              ) : currentStreak < challenge.streak_target && (
                <Button
                  onClick={handleNextQuestion}
                  className="w-full rounded-full py-6 font-medium btn-press bg-ember hover:bg-ember/90 text-white"
                >
                  Continue
                </Button>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex items-center justify-center">
      <DevBanner />
      <DevMathTestPanel />
      <Button onClick={() => navigate("/")} className="rounded-full">
        Go to Home
      </Button>
    </div>
  );
}
